import type { FC, PropsWithChildren } from 'react'
import { useMemo } from 'react'
import { useTheme } from '@emotion/react'

import styled from '@emotion/styled'

import { ChevronDownIcon, ClearIcon } from '@segment/matcha/components/Icons/Common'
import Input from '@segment/matcha/components/Input'
import SelectBase, * as Select from '@segment/matcha/components/Select'
import type { SelectState } from '@segment/matcha/components/Select/useSelect'
import useSelect from '@segment/matcha/components/Select/useSelect'

import type { Category } from '../../Catalog'

interface MobileCategoriesListProps {
  categoryId: string | null
  categories: Category[]
  onCategoryChange: (value: string | number | null) => void
}

const MobileCategoriesList: FC<PropsWithChildren<MobileCategoriesListProps>> = ({
  categories,
  categoryId,
  onCategoryChange,
}) => {
  const options = useMemo(
    () => [{ text: `All`, value: `all` }, ...categories.map((c) => ({ text: c.name, value: c.slug }))],
    [categories]
  )

  const option = useMemo(() => options.find((o) => o.value === categoryId) ?? options[0], [categoryId, options])

  const useControlledState = (state: SelectState): SelectState => ({
    ...state,
    selected: [option],
  })

  const { state, getControlProps, filteredOptions, getSelectProps } = useSelect({
    initialSelected: [option],
    useControlledState,
    options,
    id: `categories-select`,
    onSelect(o) {
      onCategoryChange(o[0]?.value ?? null)
    },
    config: {
      mobileConfirmationBehaviour: false,
    },
    label: `categories`,
  })

  const theme = useTheme()
  return (
    <Wrapper>
      <Input
        name="categories-searchbox"
        type="search"
        resetable={state.filterValue !== ``}
        expanded={true}
        clearIcon={
          <Select.ClearSelect>
            <ClearIcon size="small" color={theme.palette.neutrals.white[100]} />
          </Select.ClearSelect>
        }
        rightSecondIcon={<ChevronDownIcon />}
        {...getControlProps({ placeholder: `Select an option...` })}
      />
      <SelectBase
        id="categories-dropdown"
        name="select-example"
        options={options}
        filteredOptions={filteredOptions}
        displayMode={state.displayMode}
        open={state.open}
        {...getSelectProps()}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div``

export default MobileCategoriesList
